/**
 * Onboarding — D-rebuild v3 (Stream C).
 *
 * Replaces the wizard chrome from the previous build with a single
 * full-screen "discovery scene" pane. The page itself is a thin shell that
 * owns:
 *
 *   1. The state-machine wiring (via `useOnboarding`) that drives the
 *      DiscoveryScene's `stage` prop:
 *        dormant → igniting → live → completing → welcomed
 *   2. Tile prop assembly (id / label / icon / state / ring / angleDeg).
 *   3. Keyboard parity — Esc skips ahead to `welcomed`.
 *   4. Post-welcomed navigation: "Take the tour →" sets a sessionStorage
 *      flag that Home reads on first mount; "Skip to home" just navigates.
 *
 * No PII goes into storage — only stage + the boolean tour flag (the latter
 * managed by the Home page itself, not here directly).
 *
 * Bindle picking is intentionally absent (ADR-002).
 */
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Box,
  Boxes,
  Cable,
  Cloud,
  Code,
  GitBranch,
  Headphones,
  ListTodo,
  MessageSquare,
  Network,
  ScrollText,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react';

import DiscoveryScene from '@/components/onboarding/DiscoveryScene';
import type {
  DiscoverySceneConnector,
  ConnectorState as SceneConnectorState,
} from '@/components/onboarding/DiscoveryScene';
import type { RingNumber } from '@/components/onboarding/useOrbitalScene';
import { useOnboarding } from '@/hooks/useOnboarding';
import { ME_QUERY_KEY } from '@/contexts/AuthContext';
import type { Me } from '@/api/generated';
import {
  type ConnectorId,
  type ConnectorState as StoreConnectorState,
  useOnboardingStore,
} from '@/stores/onboarding.store';

/** Key Home reads on first mount to decide whether to auto-fire the tour. */
export const TOUR_START_STORAGE_KEY = 'wayne.tour.start';

/** Per-connector orbital placement. Stream A consumes ring + angleDeg. */
interface ConnectorLayout {
  readonly icon: LucideIcon;
  readonly ring: RingNumber;
  readonly angleDeg: number;
}

/**
 * Visual layout for the 14 connectors. Three rings with distinct angles so
 * the orbital math reads as a constellation rather than a stack.
 */
const CONNECTOR_LAYOUT: Readonly<Record<ConnectorId, ConnectorLayout>> = {
  // Ring 1 — identity + people
  phonetool: { icon: Users, ring: 1, angleDeg: 18 },
  ldap: { icon: Network, ring: 1, angleDeg: 90 },
  teams: { icon: Briefcase, ring: 1, angleDeg: 162 },
  bindles: { icon: Boxes, ring: 1, angleDeg: 234 },
  oncall: { icon: Headphones, ring: 1, angleDeg: 306 },
  // Ring 2 — code + ops
  packages: { icon: Box, ring: 2, angleDeg: 36 },
  pipelines: { icon: GitBranch, ring: 2, angleDeg: 108 },
  isengard: { icon: Cloud, ring: 2, angleDeg: 180 },
  cti: { icon: Cable, ring: 2, angleDeg: 252 },
  // Ring 3 — work + docs
  taskei: { icon: ListTodo, ring: 3, angleDeg: 0 },
  slack: { icon: MessageSquare, ring: 3, angleDeg: 72 },
  sage: { icon: Sparkles, ring: 3, angleDeg: 144 },
  quip: { icon: ScrollText, ring: 3, angleDeg: 216 },
  kingpin: { icon: Code, ring: 3, angleDeg: 288 },
};

/** Narrow our store's ConnectorState to the (smaller) scene union. */
function toSceneState(s: StoreConnectorState): SceneConnectorState {
  // The scene only renders 4 of the 5 API states; `skipped` is a
  // server-only outcome we treat as `succeeded` visually.
  return s === 'skipped' ? 'succeeded' : s;
}

export default function Onboarding(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const resetStore = useOnboardingStore((s) => s.reset);

  // Always start at dormant when the onboarding page mounts. The
  // ProtectedRoute decides WHETHER to show this page (based on hasOnboarded);
  // once you're here the visual flow must replay from the beginning.
  useEffect(() => {
    resetStore();
  }, [resetStore]);

  const {
    me,
    stage,
    connectors,
    userName,
    beginDiscovery,
    skipToWelcomed,
  } = useOnboarding();

  // Esc during live/completing → jump to welcomed. Honor the brief: "Esc at
  // any time during live = jump to welcomed". We extend the same handler to
  // `igniting` and `completing` so the user is never stuck waiting on an
  // animation.
  useEffect(() => {
    if (stage === 'dormant' || stage === 'welcomed') return;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        skipToWelcomed();
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [skipToWelcomed, stage]);

  /** Compose the per-connector props the DiscoveryScene expects. */
  const sceneConnectors = useMemo<ReadonlyArray<DiscoverySceneConnector>>(
    () =>
      connectors.map((row) => {
        const layout =
          CONNECTOR_LAYOUT[row.id as ConnectorId] ??
          ({ icon: Box, ring: 1, angleDeg: 0 } as const);
        const Icon = layout.icon;
        return {
          id: row.id,
          label: row.label,
          icon: <Icon size={14} aria-hidden="true" />,
          state: toSceneState(row.state),
          ring: layout.ring,
          angleDeg: layout.angleDeg,
        };
      }),
    [connectors],
  );

  /** Mark onboarding complete: update local cache + POST to backend. */
  const markOnboardingComplete = (): void => {
    queryClient.setQueryData<Me>(ME_QUERY_KEY, (prev) =>
      prev ? { ...prev, hasOnboarded: true } : prev,
    );
    try {
      const apiBase = import.meta.env.VITE_API_BASE ?? '/api';
      void fetch(`${apiBase}/me/onboarding`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: true }),
      });
    } catch {
      // Non-critical; the user can re-onboard from settings.
    }
  };

  const handleTakeTour = (): void => {
    markOnboardingComplete();
    try {
      window.sessionStorage.setItem(TOUR_START_STORAGE_KEY, 'home');
    } catch {
      // Best-effort; if storage is unavailable the user just lands on home
      // without the tour. We never crash navigation on a storage failure.
    }
    navigate('/');
  };

  const handleSkipTour = (): void => {
    markOnboardingComplete();
    navigate('/');
  };

  // We render the DiscoveryScene unconditionally — it's the whole page.
  // The page wrapper supplies the body background + min-height, the scene
  // owns its own internal layout including the dormant veil and the
  // welcomed overlay card.
  // Show a quiet placeholder while the `/me` query is still loading — the
  // dormant copy uses the user's name, so it would read "Hello, there." too
  // briefly. This matches the previous Onboarding's behavior.
  if (!me) {
    return (
      <div
        className="min-h-screen bg-wayne-bg text-wayne-ink flex items-center justify-center"
        role="status"
        aria-live="polite"
      >
        <span className="text-wayne-ink-3 text-[13px]">
          Loading your profile…
        </span>
      </div>
    );
  }

  // The editorial right pane is visible only during the in-flight stages
  // (igniting/live/completing). Dormant and welcomed are full-bleed.
  const showCopyPane =
    stage === 'igniting' || stage === 'live' || stage === 'completing';
  const succeededCount = connectors.filter((c) => c.state === 'succeeded').length;
  const total = connectors.length;
  const remaining = total - succeededCount;
  const etaCopy =
    stage === 'completing'
      ? 'finishing up…'
      : remaining <= 0
        ? 'done — opening Wayne…'
        : `about ${Math.max(2, Math.round(remaining * 1.3))}s remaining`;
  const meterPct = total === 0 ? 0 : Math.round((succeededCount / total) * 100);

  return (
    <div
      className="h-screen w-screen bg-wayne-bg text-wayne-ink overflow-hidden grid"
      style={{
        gridTemplateColumns: showCopyPane ? '1fr minmax(360px, 440px)' : '1fr 0px',
        transition: 'grid-template-columns 900ms var(--ease-default)',
      }}
    >
      <div className="relative overflow-hidden">
        <DiscoveryScene
          stage={stage}
          connectors={sceneConnectors}
          userName={userName}
          onIgnite={beginDiscovery}
          onTakeTour={handleTakeTour}
          onSkipTour={handleSkipTour}
        />
      </div>
      <aside
        className="relative bg-wayne-surface border-l border-wayne-hairline overflow-hidden flex flex-col gap-7 p-14"
        style={{
          opacity: showCopyPane ? 1 : 0,
          pointerEvents: showCopyPane ? 'auto' : 'none',
          transition:
            'opacity 900ms var(--ease-default) 200ms, padding 900ms var(--ease-default)',
        }}
        aria-hidden={!showCopyPane}
      >
        <div
          className="text-[10.5px] uppercase font-bold text-wayne-ink-3"
          style={{ letterSpacing: '0.18em' }}
        >
          Onboarding · Step 1 of 2
        </div>
        <h1
          className="font-serif text-[40px] font-medium leading-[1.08] m-0 text-wayne-ink"
        >
          Putting{' '}
          <em className="italic text-wayne-accent">everything</em>
          <br />
          about you
          <br />
          in one place.
        </h1>
        <p className="m-0 text-[15px] leading-[1.55] text-wayne-ink-2 max-w-[36ch]">
          We&rsquo;re connecting to Phonetool, your bindles, the AWS accounts
          you can reach, your Taskei rooms, your CTIs, the channels you live
          in, and a few more — so the agents have the same context you&rsquo;d
          give a teammate on day one.
        </p>
        <div className="flex flex-col gap-2.5" aria-label="Discovery progress">
          <div className="relative h-1.5 bg-wayne-surface-2 rounded-full overflow-hidden">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                width: `${meterPct}%`,
                background:
                  'linear-gradient(90deg, var(--wayne-accent), var(--wayne-orange))',
                transition: 'width 600ms var(--ease-spring)',
              }}
            />
          </div>
          <div className="flex justify-between text-[11px] text-wayne-ink-3 tabular-nums">
            <span>
              <strong className="text-wayne-ink font-bold">
                {succeededCount}
              </strong>{' '}
              of {total} sources
            </span>
            <span>{etaCopy}</span>
          </div>
        </div>
        <ul className="list-none m-0 p-0 grid grid-cols-2 gap-x-4 text-[13px]">
          {connectors.map((row) => (
            <li
              key={row.id}
              data-row={row.state}
              className="flex items-center gap-2.5 text-wayne-ink-2 py-1.5 border-b border-wayne-hairline last:border-b-0"
              style={{ opacity: row.state === 'pending' ? 0.45 : 1 }}
            >
              <span
                className="w-2.5 h-2.5 rounded-full flex-none border"
                style={{
                  background:
                    row.state === 'succeeded'
                      ? 'var(--wayne-green)'
                      : row.state === 'running'
                        ? 'var(--wayne-accent)'
                        : 'var(--wayne-surface-2)',
                  borderColor:
                    row.state === 'succeeded'
                      ? 'var(--wayne-green)'
                      : row.state === 'running'
                        ? 'var(--wayne-accent)'
                        : 'var(--wayne-hairline-2)',
                }}
              />
              <span className="flex-1 min-w-0 font-medium text-wayne-ink truncate">
                {row.label}
              </span>
              <span className="text-[11px] text-wayne-ink-3 tabular-nums min-w-[3ch] text-right">
                {row.state === 'succeeded' || row.state === 'skipped'
                  ? '✓'
                  : '—'}
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-auto text-[11.5px] text-wayne-ink-3 leading-[1.55]">
          Nothing leaves Amazon. All queries run server-side under your OBO
          scope. Press <span className="px-1.5 rounded border border-wayne-hairline-2 bg-wayne-bg font-mono text-[10.5px]">Esc</span> to skip — you can re-run discovery any time
          from <span className="px-1.5 rounded border border-wayne-hairline-2 bg-wayne-bg font-mono text-[10.5px]">Settings → Memory</span>.
        </div>
      </aside>
    </div>
  );
}
